import Script from "next/script";
import { ReactNode, useCallback, useEffect, useState } from "react";

import {
  ServicePaidFlag,
  ServiceReserveStatus,
  ServiceViewType,
} from "@/apis/clients";
import { Step } from "@/components/molecules/step/Step";
import {
  useConfirmInputList,
  useServiceFormUiState,
  useServiceInfo,
} from "@/states/service-form/selectors";

type Props = {
  children?: ReactNode;
  classNames?: string;
  pageName?: string;
  isPaymentMethod?: boolean;
};

/**
 * ヘッダーなしレイアウト
 * @constructor
 */
const StepHeaderLayout = ({
  children,
  classNames = "bg-theme-thin",
  pageName,
}: Props) => {
  const [stepData, setStepData] = useState<string[]>([]);
  const service = useServiceInfo();
  const serviceFormUiState = useServiceFormUiState();
  const confirmInputList = useConfirmInputList();

  const getBillingAmount = useCallback(() => {
    const tmpDetails = confirmInputList?.service?.details;

    const tmpOptions = confirmInputList?.service?.options;

    let billingAmount = 0;
    if (
      service &&
      service.type === "event-seminar" &&
      service.reserveStatus === ServiceReserveStatus.ALL
    ) {
      billingAmount =
        service.details.length > 0 && service.details[0].price
          ? service.details[0].price
          : 0;
    } else {
      if (
        service &&
        service.type === "content-webinar" &&
        service.paid_flag === ServicePaidFlag.PAID &&
        service.details
      ) {
        service.details.map((detail) => {
          billingAmount += detail.price ? detail.price : 0;
        });
      } else {
        tmpDetails?.map((detail) => {
          billingAmount += detail.price ? detail.price : 0;
        });
      }
    }
    tmpOptions?.map((option) => {
      billingAmount += option.price ? option.price : 0;
    });
    return billingAmount;
  }, [
    confirmInputList?.service?.details,
    confirmInputList?.service?.options,
    service,
  ]);

  const isPaymentFormFlg = useCallback(() => {
    if (!service) return false;

    if (service.type === "calendar") {
      if (serviceFormUiState.status === "Loaded") {
        if (!serviceFormUiState.inputs.service) return false;
        const serviceMenu = service.menus.filter(
          (menu) => menu.id === serviceFormUiState.inputs.service.serviceMenuId,
        );
        if (serviceMenu.length > 0 && serviceMenu[0].price > 0) return true;
      }
      return false;
    }
    if (service.type === "form") {
      let playFlagResult = service.details.map((detail) => {
        return detail.price && detail.price > 0;
      });

      if (service.reserveStatus === ServiceReserveStatus.ALL) {
        playFlagResult = service.details.map((detail) => {
          return detail.price && detail.price > 0;
        });
      }
      return playFlagResult.includes(true);
    }

    if (service.type === "event-seminar") {
      const totalPrice = getBillingAmount();
      return totalPrice > 0;
    }

    if (
      service.type === "content-webinar" &&
      service.paid_flag === ServicePaidFlag.PAID
    ) {
      if (!service.details) return false;
      const playFlagResult = service.details.map((detail) => {
        return detail.price && detail.price > 0;
      });
      return playFlagResult.includes(true);
    }

    if (service.type === "subscription") {
      const details = confirmInputList?.service?.details;
      if (!details) return false;
      return details.some((detail) => detail.price && detail.price > 0);
    }

    return false;
  }, [service, serviceFormUiState, getBillingAmount, confirmInputList]);

  useEffect(() => {
    if (
      service?.type === "email-newsletter" ||
      service?.type === "survey" ||
      service?.type === "document-request"
    ) {
      setStepData([]);
      return;
    }
    if (service?.type === "calendar") {
      if (isPaymentFormFlg()) {
        setStepData([
          "日程選択",
          "お客様情報の入力",
          "支払方法選択",
          "内容確認",
          "完了",
        ]);
        return;
      }
      setStepData(["日程選択", "お客様情報の入力", "内容確認", "完了"]);
      return;
    }
    if (service?.viewType === ServiceViewType.FORM) {
      if (isPaymentFormFlg()) {
        setStepData(["支払方法選択", "内容確認", "完了"]);
        return;
      }
      setStepData(["内容確認", "完了"]);
      return;
    }
    if (service?.viewType === ServiceViewType.BUTTON) {
      if (isPaymentFormFlg()) {
        setStepData(["お客様情報の入力", "支払方法選択", "内容確認", "完了"]);
        return;
      }
      setStepData(["お客様情報の入力", "内容確認", "完了"]);
      return;
    }
    if (service?.type === "subscription") {
      if (isPaymentFormFlg()) {
        setStepData(["お客様情報の入力", "支払方法選択", "内容確認", "完了"]);
        return;
      }
      setStepData(["お客様情報の入力", "内容確認", "完了"]);
      return;
    }
  }, [isPaymentFormFlg, service]);

  const [activeStep, setActiveStep] = useState<number>();

  useEffect(() => {
    setActiveStep(stepData.indexOf(pageName || ""));
  }, [pageName, stepData]);
  return (
    <>
      <Script src="/customer/assets/yoyacoo.js"></Script>
      <div className={`flex justify-center ${classNames}`}>
        {stepData.length > 0 && (
          <Step activeStep={activeStep || 0} listStep={stepData} />
        )}
        <div className="w-full">
          <main
            id="yoyacoo"
            className={`md:px-4 md:pb-4 ${
              stepData.length > 0 ? "pt-[118px] md:pt-[88px]" : ""
            }`}
          >
            {children}
          </main>
        </div>
      </div>
    </>
  );
};

export default StepHeaderLayout;
